import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';
import { getCsrfToken } from '../../../contexts/AuthContext';
import { getEnvironmentId } from '@utils/environmentId';
import type { Script, UpdateScriptRequest } from '../types';

const AUTO_SAVE_DELAY = 1000;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type UseItemAutoSaveOptions = {
  scriptId: string;
  isDirty: boolean;
  getScriptData: () => string;
  markClean: () => void;
  activeScript: Script | undefined;
  enabled?: boolean;
  /** Parent script ID — when provided, the parent's children cache entry is updated in-place */
  parentScriptId?: string | null;
};

/**
 * Build the full API URL for a script PUT request.
 * Resolves the base URL the same way bffApiClient does.
 */
const buildScriptUrl = (id: string): string => {
  const envBaseUrl = import.meta.env.VITE_VOLTBOT_API_BASE_URL;
  const base = envBaseUrl != null ? envBaseUrl || '' : 'https://localhost:5002';
  return `${base}/api/mqtt-scripts/${id}`;
};

/**
 * Last-resort save using fetch with keepalive — survives page unload.
 * Unlike axios, keepalive fetch requests are not cancelled by navigation.
 */
const beaconSave = (id: string, dto: UpdateScriptRequest): void => {
  const url = buildScriptUrl(id);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/plain'
  };

  const csrf = getCsrfToken();
  if (csrf) headers['X-CSRF-TOKEN'] = csrf;

  const envId = getEnvironmentId();
  if (envId) headers['X-Environment-Id'] = envId;

  try {
    fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
      credentials: 'include',
      keepalive: true
    }).catch(() => {});
  } catch {
    // keepalive fetch can throw synchronously if payload > 64 KiB — ignore
  }
};

export const useItemAutoSave = ({
  scriptId,
  isDirty,
  getScriptData,
  markClean,
  activeScript,
  enabled = true,
  parentScriptId
}: UseItemAutoSaveOptions) => {
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout>>();
  const savedFadeTimer = useRef<ReturnType<typeof setTimeout>>();

  // Synchronous ref updates — no effects needed, always current during render
  const getScriptDataRef = useRef(getScriptData);
  getScriptDataRef.current = getScriptData;

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const activeScriptRef = useRef(activeScript);
  activeScriptRef.current = activeScript;

  const scriptIdRef = useRef(scriptId);
  scriptIdRef.current = scriptId;

  const parentScriptIdRef = useRef(parentScriptId);
  parentScriptIdRef.current = parentScriptId;

  const markCleanRef = useRef(markClean);
  markCleanRef.current = markClean;

  // Track the data that was sent so we can detect changes during save
  const lastSentData = useRef<string>('');

  // Track whether a tanstack mutation is in-flight so unmount doesn't double-save.
  // We use a ref (not autoSave.isPending) because we need to read it synchronously
  // inside cleanup functions that run after the component has unmounted.
  const isMutationPending = useRef(false);

  // When saveNow is called while a mutation is in-flight, we queue a follow-up
  // save so the latest data is persisted once the current mutation settles.
  const saveRequestedWhilePending = useRef(false);

  const updateCaches = useCallback(
    (data: Script) => {
      queryClient.setQueryData(['scripts', data.id], data);
      const parentId = parentScriptIdRef.current;
      if (parentId) {
        queryClient.setQueryData<Script[]>(
          ['scripts', 'children', parentId],
          old => {
            if (!old) {
              console.warn(
                `[AUTOSAVE] updateCaches: children cache is EMPTY for parent=${parentId}, skipping update`
              );
              return old;
            }
            console.log(
              `[AUTOSAVE] updateCaches: updating child ${data.id} in parent=${parentId}, childCount=${old.length}`
            );
            return old.map(child => (child.id === data.id ? data : child));
          }
        );
      }
    },
    [queryClient]
  );

  // Use a ref for mutate so startSave can be stable
  const mutateRef = useRef<
    (vars: { id: string; dto: UpdateScriptRequest }) => void
  >(() => {});

  const autoSave = useMutation<
    Script,
    Error,
    { id: string; dto: UpdateScriptRequest }
  >({
    mutationFn: async ({ id, dto }) => {
      const response = await bffApiClient.put<Script>(
        `/api/mqtt-scripts/${id}`,
        dto
      );
      return response.data;
    },
    onMutate: async ({ id, dto }) => {
      isMutationPending.current = true;
      console.log(`[AUTOSAVE] onMutate scriptId=${id} type=${dto.scriptType}`, {
        dataPreview: dto.scriptData?.substring(0, 120)
      });
      // Cancel any in-flight refetches for this script so they don't land
      // after our save and overwrite the cache with stale server data.
      await queryClient.cancelQueries({
        queryKey: ['scripts', scriptIdRef.current]
      });
    },
    onSuccess: data => {
      console.log(`[AUTOSAVE] onSuccess scriptId=${data.id}`, {
        dataPreview: data.scriptData?.substring(0, 120),
        parentId: parentScriptIdRef.current
      });
      updateCaches(data);

      // Only mark clean if no new changes arrived during the save.
      // Compare current builder output against what we sent — if they match,
      // no edits happened mid-flight and we can safely clear the dirty flag.
      const currentData = getScriptDataRef.current();
      if (currentData === lastSentData.current) {
        markCleanRef.current();
      }
      // If they differ, isDirty stays true and the effect will re-queue
      // because autoSave.isPending is now in the dependency array.

      setSaveStatus('saved');
      clearTimeout(savedFadeTimer.current);
      savedFadeTimer.current = setTimeout(() => setSaveStatus('idle'), 3000);
    },
    onError: err => {
      console.error(`[AUTOSAVE] onError scriptId=${scriptIdRef.current}`, err);
      setSaveStatus('error');
    },
    onSettled: () => {
      isMutationPending.current = false;

      // If saveNow was called while this mutation was in-flight, fire the
      // queued save now so the latest data reaches the server.
      if (saveRequestedWhilePending.current) {
        saveRequestedWhilePending.current = false;
        console.log(
          `[AUTOSAVE] firing deferred save for scriptId=${scriptIdRef.current}`
        );
        // Defer to let React process state updates from onSuccess first
        setTimeout(() => startSave(), 0);
      }
    }
  });

  mutateRef.current = autoSave.mutate;

  // Stable save function — uses only refs, never stale
  const startSave = useCallback(() => {
    const script = activeScriptRef.current;
    if (!script) return;
    const scriptData = getScriptDataRef.current();
    lastSentData.current = scriptData;
    setSaveStatus('saving');
    mutateRef.current({
      id: scriptIdRef.current,
      dto: {
        globalId: script.globalId,
        scriptType: script.scriptType,
        scriptData,
        createdBy: script.createdBy
      }
    });
  }, []);

  // Save immediately — cancels any pending debounce timer and fires now.
  // Use this when changes must be persisted right away (e.g. cell assignments
  // after item creation) rather than waiting for the debounce delay.
  // If a mutation is already in-flight, the save is queued and fires
  // automatically when the current mutation settles (see onSettled).
  const saveNow = useCallback(() => {
    clearTimeout(autoSaveTimer.current);
    if (autoSave.isPending) {
      saveRequestedWhilePending.current = true;
    } else {
      startSave();
    }
  }, [autoSave.isPending, startSave]);

  // Main autosave effect.
  // Uses !!activeScript (boolean) instead of the object reference so that
  // query refetches (which produce a new object) don't reset the debounce
  // timer.  The actual script data is read from activeScriptRef inside
  // startSave, so it is always up-to-date.
  const hasActiveScript = !!activeScript;
  useEffect(() => {
    if (!enabled || !isDirty || !hasActiveScript || autoSave.isPending) return;

    console.log(
      `[AUTOSAVE] scheduling save in ${AUTO_SAVE_DELAY}ms for scriptId=${scriptIdRef.current} type=${activeScriptRef.current?.scriptType}`
    );
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(startSave, AUTO_SAVE_DELAY);

    return () => clearTimeout(autoSaveTimer.current);
  }, [
    enabled,
    isDirty,
    hasActiveScript,
    scriptId,
    autoSave.isPending,
    startSave
  ]);

  // beforeunload — silently fire a keepalive save so the browser doesn't
  // discard dirty data on refresh / tab close.  We intentionally do NOT show
  // the browser "Leave site?" dialog — the keepalive fetch is enough and the
  // dialog is disruptive during normal editing.
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isDirtyRef.current) return;

      const script = activeScriptRef.current;
      if (script) {
        beaconSave(scriptIdRef.current, {
          globalId: script.globalId,
          scriptType: script.scriptType,
          scriptData: getScriptDataRef.current(),
          createdBy: script.createdBy
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Cleanup on unmount — use keepalive fetch instead of axios so the request
  // survives if the unmount is caused by navigation.  Skip if a tanstack
  // mutation is already in-flight (it completes independently of the component).
  useEffect(() => {
    return () => {
      clearTimeout(autoSaveTimer.current);
      clearTimeout(savedFadeTimer.current);

      if (!isDirtyRef.current || isMutationPending.current) {
        console.log(
          `[AUTOSAVE] cleanup skip scriptId=${scriptIdRef.current} isDirty=${isDirtyRef.current} mutationPending=${isMutationPending.current}`
        );
        return;
      }

      const script = activeScriptRef.current;
      if (!script) return;

      console.log(
        `[AUTOSAVE] cleanup beaconSave scriptId=${scriptIdRef.current} type=${script.scriptType}`
      );
      beaconSave(scriptIdRef.current, {
        globalId: script.globalId,
        scriptType: script.scriptType,
        scriptData: getScriptDataRef.current(),
        createdBy: script.createdBy
      });
    };
  }, []);

  return { saveStatus, saveNow };
};
